import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import Header from './components/Layout/Header';
import Footer from './components/Layout/Footer';
import ScrollToTop from './components/Layout/ScrollToTop';
import ModernDashboard from './components/Dashboard/ModernDashboard';
import SiteSpecificDashboard from './components/Dashboard/SiteSpecificDashboard';
import ModernChampionshipsList from './components/Championships/ModernChampionshipsList';
import ChampionshipDetail from './components/Championships/ChampionshipDetail';
import { ChampionshipHistory } from './components/Championships';
import ModernTeamsPage from './components/Teams/ModernTeamsPage';
import TeamDetail from './components/Teams/TeamDetail';
import ScoresPage from './components/Scores/ScoresPage';
import ScoreDetail from './components/Scores/ScoreDetail';
import SeasonsPage from './components/Seasons/SeasonsPage';
import SeasonDetail from './components/Seasons/SeasonDetail';
import StandingsPage from './components/Standings/StandingsPage';
import PlayersPage from './components/Players/PlayersPage';
import PlayerDetail from './components/Players/PlayerDetail';
import CoachesPage from './components/Coaches/CoachesPage';
import CoachDetail from './components/Coaches/CoachDetail';
import AnalyticsPage from './components/Analytics/AnalyticsPage';
import DataQualityPage from './components/DataQuality/DataQualityPage';
import FeaturesPage from './components/Features/FeaturesPage';
import SiteDebug from './components/Debug/SiteDebug';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import CookiePolicy from './pages/CookiePolicy';
import { SiteProvider } from './contexts/SiteContext';
import { SeasonProvider } from './contexts/SeasonContext';
// import { AuthProvider } from './contexts/AuthContext';
// import AuthCallback from './pages/AuthCallback';
import { useFavicon } from './hooks/useFavicon';
import { useDynamicCSSVariables } from './hooks/useDynamicCSS';
import './index.css';

import { CACHE_CONFIG, cacheSize } from './utils/cacheConfig';
import { getCacheManager } from './utils/cacheManager';

// Create a client with advanced caching settings
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error: any) => {
        // Don't retry on 4xx errors
        if (error?.status >= 400 && error?.status < 500) {
          return false;
        }
        // Retry up to 2 times for other errors
        return failureCount < 2;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      // Use dynamic cache configuration
      staleTime: CACHE_CONFIG.DYNAMIC.staleTime,
      cacheTime: CACHE_CONFIG.DYNAMIC.cacheTime,
      // Network mode optimizations
      networkMode: 'offlineFirst',
    },
    mutations: {
      retry: 1,
      networkMode: 'online',
    },
  },
  // Advanced cache management
  queryCache: new QueryCache({
    onError: (error: any, query) => {
      console.error('Query error:', error, 'for query:', query.queryKey);
    },
    onSuccess: (data, query) => {
      // Log successful cache hits for monitoring
      if (process.env.NODE_ENV === 'development') {
        console.log('Cache hit for:', query.queryKey);
      }
    },
  }),
  mutationCache: new MutationCache({
    onError: (error: any, _variables, _context, mutation) => {
      console.error('Mutation error:', error, 'for mutation:', mutation.options.mutationKey);
    },
  }),
});

// Initialize cache manager
const cacheManager = getCacheManager(queryClient);

// Setup background refresh and cache warmup
if (typeof window !== 'undefined') {
  // Warmup cache on app start
  cacheManager.warmupCache().catch(console.error);

  // Setup background refresh for real-time data
  cacheManager.setupBackgroundRefresh();

  // Garbage collection every hour
  setInterval(() => {
    cacheManager.garbageCollect().catch(console.error);
  }, 60 * 60 * 1000);

  // Optimize cache based on connection
  if ('connection' in navigator) {
    const connection = (navigator as any).connection;
    if (connection) {
      const connectionType = connection.effectiveType === '4g' ? 'fast' :
                           connection.effectiveType === 'slow-2g' ? 'slow' : 'fast';
      cacheManager.optimizeForConnection(connectionType);
    }
  }
}

// Page transition variants
const pageVariants = {
  initial: { 
    opacity: 0, 
    y: 20,
    scale: 0.98
  },
  in: { 
    opacity: 1, 
    y: 0,
    scale: 1,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  },
  out: { 
    opacity: 0, 
    y: -20,
    scale: 1.02,
    transition: {
      duration: 0.3,
      ease: [0.55, 0.055, 0.675, 0.19]
    }
  }
};

// Animated route wrapper
const AnimatedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <motion.div
    initial="initial"
    animate="in"
    exit="out"
    variants={pageVariants}
    className="min-h-screen"
  >
    {children}
  </motion.div>
);

// App content wrapper component that uses the favicon hook
function AppContent() {
  // Apply favicon and document head updates
  useFavicon();

  // Apply dynamic site-specific CSS
  useDynamicCSSVariables();

  return (
    <Router>
      <ScrollToTop />
      <div className="min-h-screen bg-gradient-dark text-gray-100 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-1/2 -right-1/2 w-full h-full bg-gradient-to-br from-primary-600/5 to-secondary-600/5 rounded-full blur-3xl" />
          <div className="absolute -bottom-1/2 -left-1/2 w-full h-full bg-gradient-to-tr from-secondary-600/5 to-accent-600/5 rounded-full blur-3xl" />
        </div>
        
        <div className="relative z-10">
          <Header />
          
          <main>
            <AnimatePresence mode="wait">
              <Routes>
                {/* <Route path="/auth/callback" element={<AuthCallback />} /> */}
                <Route path="/" element={
                  <AnimatedRoute>
                    <ModernDashboard />
                  </AnimatedRoute>
                } />
                <Route path="/championships" element={
                  <AnimatedRoute>
                    <div className="px-4 sm:px-6 lg:px-8">
                      <ModernChampionshipsList />
                    </div>
                  </AnimatedRoute>
                } />
                <Route path="/championships/:slug" element={
                  <AnimatedRoute>
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                      <ChampionshipDetail />
                    </div>
                  </AnimatedRoute>
                } />
                <Route path="/champions" element={
                  <AnimatedRoute>
                    <div className="px-4 sm:px-6 lg:px-8 py-8">
                      <ChampionshipHistory />
                    </div>
                  </AnimatedRoute>
                } />
                <Route path="/teams" element={
                  <AnimatedRoute>
                    <div className="px-4 sm:px-6 lg:px-8">
                      <ModernTeamsPage />
                    </div>
                  </AnimatedRoute>
                } />
                <Route path="/teams/:slug" element={
                  <AnimatedRoute>
                    <TeamDetail />
                  </AnimatedRoute>
                } />
                <Route path="/teams/:slug/:year" element={
                  <AnimatedRoute>
                    <TeamDetail />
                  </AnimatedRoute>
                } />
                <Route path="/scores" element={
                  <AnimatedRoute>
                    <div className="px-4 sm:px-6 lg:px-8">
                      <ScoresPage />
                    </div>
                  </AnimatedRoute>
                } />
                <Route path="/scores/:id" element={
                  <AnimatedRoute>
                    <ScoreDetail />
                  </AnimatedRoute>
                } />
                <Route path="/seasons" element={
                  <AnimatedRoute>
                    <div className="px-4 sm:px-6 lg:px-8">
                      <SeasonsPage />
                    </div>
                  </AnimatedRoute>
                } />
                <Route path="/seasons/:slug" element={
                  <AnimatedRoute>
                    <SeasonDetail />
                  </AnimatedRoute>
                } />
                <Route path="/standings" element={
                  <AnimatedRoute>
                    <div className="px-4 sm:px-6 lg:px-8">
                      <StandingsPage />
                    </div>
                  </AnimatedRoute>
                } />
                <Route path="/players" element={
                  <AnimatedRoute>
                    <div className="px-4 sm:px-6 lg:px-8">
                      <PlayersPage />
                    </div>
                  </AnimatedRoute>
                } />
                <Route path="/players/:id" element={
                  <AnimatedRoute>
                    <PlayerDetail />
                  </AnimatedRoute>
                } />
                <Route path="/coaches" element={
                  <AnimatedRoute>
                    <div className="px-4 sm:px-6 lg:px-8">
                      <CoachesPage />
                    </div>
                  </AnimatedRoute>
                } />
                <Route path="/coaches/:id" element={
                  <AnimatedRoute>
                    <CoachDetail />
                  </AnimatedRoute>
                } />
                <Route path="/analytics" element={
                  <AnimatedRoute>
                    <div className="px-4 sm:px-6 lg:px-8">
                      <AnalyticsPage />
                    </div>
                  </AnimatedRoute>
                } />
                <Route path="/data-quality" element={
                  <AnimatedRoute>
                    <div className="px-4 sm:px-6 lg:px-8">
                      <DataQualityPage />
                    </div>
                  </AnimatedRoute>
                } />
                <Route path="/features" element={
                  <AnimatedRoute>
                    <div className="px-4 sm:px-6 lg:px-8">
                      <FeaturesPage />
                    </div>
                  </AnimatedRoute>
                } />
                <Route path="/debug" element={
                  <AnimatedRoute>
                    <div className="px-4 sm:px-6 lg:px-8">
                      <SiteDebug />
                    </div>
                  </AnimatedRoute>
                } />
                {/* Legal Pages */}
                <Route path="/privacy" element={
                  <AnimatedRoute>
                    <PrivacyPolicy />
                  </AnimatedRoute>
                } />
                <Route path="/terms" element={
                  <AnimatedRoute>
                    <TermsOfService />
                  </AnimatedRoute>
                } />
                <Route path="/cookies" element={
                  <AnimatedRoute>
                    <CookiePolicy />
                  </AnimatedRoute>
                } />
              </Routes>
            </AnimatePresence>
          </main>
          
          <Footer />
        </div>
        
        {/* Enhanced toast notifications */}
        <Toaster 
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: 'rgba(30, 41, 59, 0.95)',
              color: '#f1f5f9',
              border: '1px solid rgba(99, 102, 241, 0.2)',
              borderRadius: '12px',
              backdropFilter: 'blur(16px)',
              fontSize: '14px',
              padding: '12px 16px',
            },
            success: {
              iconTheme: {
                primary: '#10b981',
                secondary: '#f1f5f9',
              },
            },
            error: {
              iconTheme: {
                primary: '#ef4444',
                secondary: '#f1f5f9',
              },
            },
            loading: {
              iconTheme: {
                primary: '#6366f1',
                secondary: '#f1f5f9',
              },
            },
          }}
        />
      </div>
    </Router>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      {/* <AuthProvider> */}
        <SiteProvider>
          <SeasonProvider>
            <AppContent />
          </SeasonProvider>
        </SiteProvider>
      {/* </AuthProvider> */}
    </QueryClientProvider>
  );
}

export default App;
